<template>
  <div>
    <!-- CustomTooltip/question-mark (filter.product.js's `tips` entries) for the filters that have one. -->
    <custom-tooltip
      v-if="tip"
      :css-class="['pointer']"
      :section="'search-new-filters-' + filterKey"
      :tooltip-text="$t(tip)"
    ></custom-tooltip>
    <custom-slider
      v-if="bounds"
      :value="sliderValue"
      :min="bounds.min"
      :max="bounds.max"
      :interval="bounds.interval"
      :data="bounds.data"
      tooltip="none"
      @update:value="onSliderChange"
    ></custom-slider>
    <div v-if="bounds" class="float-right">({{ hintLabel }})</div>

    <!-- RangeDate (e.g. IssueDate/MaturityDate, IC-specific) — same Datepicker as
         Perimeter's window mode, for a consistent look. Unlike Perimeter's mode-switch defaults
         (which seed today/last-month on purpose), a plain RangeDate has no "mode"
         concept — from/to simply start unset and stay unset until the user picks a date; no default. -->
    <div v-else-if="filterType === 'RangeDate'" class="fsb-range-row">
      <div>
        <label class="fsb-label">{{ fromLabel }}</label>
        <datepicker :value.sync="dateFrom" clear-button></datepicker>
      </div>
      <div>
        <label class="fsb-label">{{ toLabel }}</label>
        <datepicker :value.sync="dateTo" clear-button></datepicker>
      </div>
    </div>

    <!-- No known slider bounds for this filter_key (e.g. other IC-specific ranges) — plain from/to inputs. -->
    <div v-else class="fsb-range-row">
      <input
        :type="inputType"
        :step="filterType === 'RangeInt' ? 1 : 'any'"
        :value="value ? value.from : null"
        :placeholder="$t('search.rangeFrom')"
        class="fsb-range-input"
        @change="(e) => emitChange(e.target.value, value ? value.to : null)"
      />
      <input
        :type="inputType"
        :step="filterType === 'RangeInt' ? 1 : 'any'"
        :value="value ? value.to : null"
        :placeholder="$t('search.rangeTo')"
        class="fsb-range-input"
        @change="(e) => emitChange(value ? value.from : null, e.target.value)"
      />
    </div>
  </div>
</template>

<script>
import CustomSlider from '../../Lib/CustomSlider'
import CustomTooltip from '../../Lib/CustomTooltip'
import Datepicker from '../../Lib/DatePicker'
import { rhpToYears } from '../../../utils/formatter'
import { createNumberSerie } from '../../../imports/lib'

// Same `tips` entries as filter.product.js — RHP has none.
const TIPS = {
  TotalCostsRange: 'search.tips.totalCosts',
  RiyRange: 'search.tips.RIY',
  InsuredEventRange: 'search.tips.increment',
}

// The RHP/total-cost/RIY/insured-event-increment hints show a literal placeholder — not a formatted
// number — for whichever side is unset (the slider's unbounded extreme). For the three percent
// fields, an explicit 0 on the "from" side ALSO counts as unset (`tcF == null || tcF == 0`).
const NULL_PLACEHOLDERS = {
  RhpRange: { to: '> 20y' },
  TotalCostsRange: { from: '< 0%', to: '> 10%', zeroFromIsUnset: true },
  RiyRange: { from: '< 0%', to: '> 5%', zeroFromIsUnset: true },
  InsuredEventRange: { from: '< 0%', to: '> 5%', zeroFromIsUnset: true },
}

// Generic Range filter. SLIDER_BOUNDS hardcodes a slider (min/max/interval) per specific
// filter_key so known keys get a CustomSlider widget; other filter_keys
// (mostly IC-specific ranges) fall back to plain from/to inputs — there is no bound to slide within.
const SLIDER_BOUNDS = {
  // The RHP scale isn't a uniform 0.5 step across the whole 0-21 range — it's half-year steps
  // up to 5 years, then whole-year steps from 6 to 21.
  RhpRange: { min: 0, max: 21, isYears: true, data: [...createNumberSerie(0, 5, 0.5), ...createNumberSerie(6, 21, 1)] },
  TotalCostsRange: { min: -0.005, max: 0.105, interval: 0.005, isPercent: true },
  RiyRange: { min: -0.005, max: 0.055, interval: 0.005, isPercent: true },
  InsuredEventRange: { min: -0.005, max: 0.055, interval: 0.005, isPercent: true },
  // get_retailInvestorAgeLabel formats both endpoints via rhpToYears, not a plain integer.
  TMRetailInvestorAge: { min: 1, max: 100, interval: 1, isYears: true },
  // get_limitCapitalLossLevelLabel/get_ai*PercentageLabel/get_aiIntermediaryCostsLabel use
  // Math.round(v * 100) + '%' — NOT $n(v, 'percent'), which would add decimals ("45,00%").
  TMLimitedCapitalLossLevel: { min: 0, max: 1, interval: 0.01, isRoundPercent: true },
  'AdditionalInfo.SinglePremiumAmount': { min: 0, max: 500000, interval: 1000, isCurrency: true },
  'AdditionalInfo.RecurrentPremiumAmount': { min: 0, max: 10000, interval: 50, isCurrency: true },
  'AdditionalInfo.AllocationToSegregatedFund': { min: 0, max: 1, interval: 0.01, isRoundPercent: true },
  'AdditionalInfo.AllocationToUnitLinked': { min: 0, max: 1, interval: 0.01, isRoundPercent: true },
  // get_aiDurationPaymentLabel formats both endpoints via rhpToYears.
  'AdditionalInfo.PremiumPayedPeriod': { min: 1, max: 30, interval: 1, isYears: true },
  'AdditionalInfo.IntermediaryCosts': { min: 0, max: 1, interval: 0.01, isRoundPercent: true },
  // IC-specific. import_mapping_items has percentage_format = 'DecimalFraction' for this column
  // (1.0 = 100% capital protection) — same convention as AllocationToSegregatedFund/UnitLinked.
  CapitalProtection: { min: 0, max: 1, interval: 0.01, isRoundPercent: true },
  // IC-specific. No 'DecimalFraction' tag on either column, but InvestmentCertificateRow's own
  // class doc says why: "Percentage-like values are already fractions in the IC CSV (no
  // DecimalFraction rescale)" — the tag is only needed to RESCALE a raw "80" into 0.80 on import;
  // here the CSV already carries the fraction, so no tag, but the value (barrier as % of the
  // strike/initial price) is exactly the same 0-1 fraction convention as CapitalProtection.
  BarrierLevel: { min: 0, max: 1, interval: 0.01, isRoundPercent: true },
  CouponBarrierLevel: { min: 0, max: 1, interval: 0.01, isRoundPercent: true },
}

export default {
  name: 'rangeFilter',
  components: { CustomSlider, CustomTooltip, Datepicker },
  props: {
    filterKey: { type: String, required: true },
    filterType: { type: String, required: true },
    value: { type: Object, default: null },
    // Followed page: when set, replaces the separate outer filter label + generic
    // "Da"/"A" labels with a single combined label per side (e.g. "Data di emissione Da"), so the
    // filter shows one label row instead of two. Unset everywhere else (the search sidebar keeps
    // its own outer label + generic "Da"/"A").
    label: { type: String, default: null },
  },
  computed: {
    bounds() {
      return SLIDER_BOUNDS[this.filterKey] || null
    },
    fromLabel() {
      return this.label ? `${this.label} ${this.$t('search.rangeFrom')}` : this.$t('search.rangeFrom')
    },
    toLabel() {
      return this.label ? `${this.label} ${this.$t('search.rangeTo')}` : this.$t('search.rangeTo')
    },
    tip() {
      return TIPS[this.filterKey] || null
    },
    inputType() {
      return this.filterType === 'RangeDate' ? 'date' : 'number'
    },
    dateFrom: {
      get() {
        return this.value && this.value.from ? new Date(this.value.from) : null
      },
      set(newDate) {
        this.emitChange(toIsoDate(newDate), this.value ? this.value.to : null)
      },
    },
    dateTo: {
      get() {
        return this.value && this.value.to ? new Date(this.value.to) : null
      },
      set(newDate) {
        this.emitChange(this.value ? this.value.from : null, toIsoDate(newDate))
      },
    },
    sliderValue() {
      if (!this.bounds) return [0, 0]
      const from = this.value && this.value.from !== null && this.value.from !== undefined ? this.value.from : this.bounds.min
      const to = this.value && this.value.to !== null && this.value.to !== undefined ? this.value.to : this.bounds.max
      return [from, to]
    },
    hintLabel() {
      if (!this.bounds) return ''
      const placeholders = NULL_PLACEHOLDERS[this.filterKey] || {}
      const fmt = (v) => {
        if (this.bounds.isPercent) return this.$n(v, 'percent')
        if (this.bounds.isRoundPercent) return Math.round(v * 100) + '%'
        if (this.bounds.isCurrency) return this.$n(v, 'currency')
        if (this.bounds.isYears) return rhpToYears(v)
        return v
      }
      const rawFrom = this.value && this.value.from !== null && this.value.from !== undefined ? this.value.from : null
      const rawTo = this.value && this.value.to !== null && this.value.to !== undefined ? this.value.to : null
      const fromIsUnset = rawFrom === null || (placeholders.zeroFromIsUnset && rawFrom === 0)
      const fromText = fromIsUnset ? placeholders.from || fmt(this.bounds.min) : fmt(rawFrom)
      const toText = rawTo === null ? placeholders.to || fmt(this.bounds.max) : fmt(rawTo)
      // A single-value selection shows one value, not "X - X".
      return fromText === toText ? fromText : `${fromText} - ${toText}`
    },
  },
  methods: {
    onSliderChange(newValue) {
      const [from, to] = newValue
      this.$emit('input', { from: from === this.bounds.min ? null : from, to: to === this.bounds.max ? null : to })
    },
    emitChange(rawFrom, rawTo) {
      const parse = (v) => {
        if (v === null || v === undefined || v === '') return null
        return this.filterType === 'RangeDate' ? v : Number(v)
      }
      const from = parse(rawFrom)
      const to = parse(rawTo)
      this.$emit('input', from === null && to === null ? null : { from, to })
    },
  },
}

function toIsoDate(date) {
  if (!date) return null
  // Local date components, NOT `toISOString()` (which converts to UTC): the Datepicker builds
  // Date objects at LOCAL midnight for the selected calendar day, so in any timezone ahead of
  // UTC (e.g. CET/CEST), `toISOString()` rolls that back to the PREVIOUS day. That day-shift is
  // silent with a plain input, but becomes a real infinite loop with the current Datepicker
  // (Lib/DatePicker.vue, flatpickr-based): its value prop is compared by formatted string, so a
  // shifted round-trip never matches what's displayed, forcing another `setDate`, which shifts
  // the day again, forever.
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
</script>

<style scoped>
.fsb-range-input {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  height: 30px;
  padding: 0 9px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  font-size: 12.5px;
  color: #374151;
  font-family: 'Inter', sans-serif;
  outline: none;
  background: #f9fafb;
}
.fsb-range-input:focus {
  border-color: #e05537;
  background: white;
}
/* The hint only needs float-right — no font-size, color, or font-family override. It just
   inherits the ambient text style, so .range-hint intentionally carries no rules here. */
</style>
